import styles from './MonitorPortfolioTable.module.scss';
import { GridSortDirection, GridSortItem } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services';
import { useAuth } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';
import { getEsDirectionFromMui } from 'store/services/es-query-helpers';
import { useDispatch } from 'react-redux';
import { setViewAsSupplier } from 'store/features/viewAsSupplier';
import { DataTable, DataTableProps } from 'components/data/DataTable/DataTable';

export interface MonitorPortfolioTableProps {
    columns: DataTableProps['columns'];
    /** Used as data table id, must match with a session entry key. Also used to name exported file. */
    tableName:
        | 'offlineMonitors'
        | 'lowBatteryMonitors'
        | 'shippedMonitors'
        | 'sleepingMonitors';
    /** The monitor portfolio table should only show monitors with this status.*/
    statusFilter: 'deployed' | 'sleep' | 'shipped';
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
    /** Whether or not to show only deployed devices with battery_crit < 0 or battery_warn < 0. */
    lowBatteryOnly?: boolean;
    /** Whether or not to show only offline deployed devices. */
    offlineOnly?: boolean;
}

export default function MonitorPortfolioTable({
    columns,
    tableName,
    statusFilter,
    viewAsSupplier,
    lowBatteryOnly = false,
    offlineOnly = false,
}: MonitorPortfolioTableProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [query, { data, isLoading, isFetching }] =
        useLazyGetSupplierDevicesEsQuery();
    const [exportQuery] = useLazyGetSupplierDevicesEsQuery();

    // Data Filtering
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortModel, setSortModel] = useState<GridSortItem[]>([]);
    const [sortField, setSortField] = useState<string>();
    const [sortDirection, setSortDirection] = useState<GridSortDirection>();
    const [queryConfig, setQueryConfig] =
        useState<ElasticsearchDeviceBodyConfig>();

    const queryOptions = useMemo(
        () => ({
            currentPage,
            pageSize,
            sortField,
            sortDirection,
        }),
        [currentPage, pageSize, sortField, sortDirection]
    );

    // Update ES query params.
    useEffect(() => {
        if (queryOptions && statusFilter) {
            setQueryConfig({
                limit: queryOptions.pageSize,
                offset: queryOptions.currentPage * queryOptions.pageSize,
                direction: getEsDirectionFromMui(
                    queryOptions.sortDirection ?? ''
                ),
                sortField: queryOptions.sortField ?? 'name',
                statusFilter,
                offlineOnly,
                excludeVirtualDevices: true,
                lowBatteryOnly,
                isAdminUser: user?.protected?.supplierId?.toString() === '0',
            });
        }
    }, [
        queryOptions,
        statusFilter,
        lowBatteryOnly,
        offlineOnly,
        user?.protected?.supplierId,
    ]);

    // Update sort variables on sort model change.
    useEffect(() => {
        if (sortModel[0]) {
            setSortDirection(sortModel[0].sort);
            setSortField(sortModel[0].field);
        }
    }, [sortModel]);

    // Get all monitors for the supplier.
    useEffect(() => {
        if (user?.uid && user?.protected && queryConfig) {
            const supplierId = viewAsSupplier ?? user.protected.supplierId;

            // Don't query for devices for the admin account.
            if (!supplierId || supplierId === '0') return;

            query({
                userUid: user.uid,
                supplierId,
                queryConfig: queryConfig,
            });
        }
    }, [user?.protected, user?.uid, queryConfig, viewAsSupplier, query]);

    // Set the viewAsSupplier value.
    useEffect(() => {
        if (user?.protected?.supplierId) {
            dispatch(
                setViewAsSupplier(viewAsSupplier ?? user.protected.supplierId)
            );
        }
    }, [viewAsSupplier, user?.protected?.supplierId, dispatch]);

    return (
        <div
            className={styles.MonitorPortfolioTable}
            data-testid="MonitorPortfolioTable"
        >
            <DataTable
                id={tableName}
                autoHeight
                sx={{
                    '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                    },
                }}
                getRowHeight={() => 'auto'}
                onRowClick={(params) =>
                    navigate(`/device-details/${params.row.deviceId}`, {
                        state: { device: params?.row },
                    })
                }
                // NOTE: using the csvOptions enables us to export through ES, which in turn enables us to
                // export ALL matching devices in the table, not just the devices that are currently displayed (RE pagination).
                // However, this also means that the table columns are not being exported so columns that include supplemental data acquired through non-device data (e.g., shipping date, tracking link, etc)
                // will not be included in the export as is. If this becomes an issue in the future and those fields are wanted in the export, look there first.
                csvOptions={{
                    query: exportQuery,
                    // Mostly copying from the queryOptions but we don't want to include any offset/limit info because we want to export whole collection.
                    queryConfig: {
                        ...queryConfig,
                        ...{
                            limit: undefined,
                            offset: undefined,
                        },
                    },
                    fileName: `tank-utility-${tableName}`,
                    supplierId: viewAsSupplier,
                }}
                loading={isFetching || isLoading}
                columns={columns}
                data={data ?? []}
                getRowId={(device) => device.deviceId}
                rowsPerPageOptions={[10, 20, 50]}
                pageSize={pageSize}
                onPageChange={(newPage) => {
                    setCurrentPage(newPage);
                }}
                onPageSizeChange={(pageSize) => {
                    setPageSize(pageSize);
                }}
                onSortModelChange={(changes) => {
                    setSortModel(changes);
                }}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'name', sort: 'asc' }],
                    },
                }}
                rowCount={data?.total ?? 0}
                paginationMode="server"
                sortingMode="server"
                onCellClick={(params, e) => {
                    if (!!params && params.field === 'trackingLink') {
                        // Don't nav to device details if the user clicks on the tracking link.
                        e.stopPropagation();
                    }
                }}
                disableDensitySelector
                disableColumnMenu
                disableColumnFilter
            />
        </div>
    );
}
